import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,
    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  calloutWarn: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255, 180, 80, 0.35)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  layoutGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
    marginTop: 10,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  layoutBox: {
    padding: 12,
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.6,
    border: '1px solid rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    color: '#e8f5ff',
  },
  layoutLabel: {
    fontWeight: 800,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 5,
  },
  primaryLabel: { color: '#9ef7c5' },
  suppLabel: { color: '#9ea4d8' },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
}));

export default function TransactionsGuidePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Evidence Graph Guide</div>
            <div className={classes.subtitle}>
              A practical walkthrough for using the /graphs Evidence Graph — how to navigate the live 3D transaction graph, switch layouts, investigate nodes, and integrate with Stories and Theses.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/transactions-use-cases')}>
              Use Cases
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/graphs')}>
              Open Graph
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1) What the Evidence Graph is</div>
        <div className={classes.body}>
          {'The /links view is E3D\'s live 3D transaction graph — the primary investigation environment for on-chain activity. It is not just a visualization: it\'s an interactive tool for tracing funds, identifying coordination patterns, and drilling into raw transaction structure.\n\nEvery node is a wallet, token, or contract. Every edge is a transaction. The graph updates in real time (LIVE mode) or shows a historical snapshot for a selected time window.\n\nThe Evidence Graph serves three roles:\n- Exploration: watch live transaction structure evolve in real time to spot anomalies as they form\n- Investigation: drill into story evidence — nodes, clusters, funnels, loops — from story alerts\n- Validation: confirm or refute the structural claims made in Stories and Theses by inspecting the underlying graph topology'}
        </div>
        <div className={classes.callout}>
          {'The /links route is also called the "Evidence Graph" in the UI. When a story says "drill into /links", this is the destination. It should be your first stop after opening a Story\'s Evidence Summary.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2) Primary vs. Supplementary Layouts</div>
        <div className={classes.body}>
          {'The layout you use shapes what patterns are visible. Choosing the right layout for your investigation goal is as important as the data itself.\n'}
        </div>
        <div className={classes.layoutGrid}>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.primaryLabel}`}>Organic Galaxy — Primary</div>
            {'The default layout and the primary transaction graph view. Uses a physics-based force simulation to position nodes by their actual relationship strength. Clusters, hubs, funnels, and starburst patterns are naturally visible without any configuration.\n\nAlways start here. Return here to validate any pattern you spotted in a supplementary layout.'}
          </div>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.suppLabel}`}>Tube — Supplementary</div>
            {'Arranges nodes along a cylindrical tube. Useful for revealing linear chains and sequential transaction flows that appear tangled in Organic Galaxy. Best for: tracing hop-by-hop fund routing.'}
          </div>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.suppLabel}`}>Sphere — Supplementary</div>
            {'Distributes nodes on the surface of a sphere. Useful for identifying hubs — nodes that connect to many others — because high-degree nodes become visually prominent against the spherical backdrop. Best for: finding whale addresses and high-connectivity routers.'}
          </div>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.suppLabel}`}>Grid / Grid-Cube — Supplementary</div>
            {'Places nodes in a regular grid structure. Useful for comparing groups of addresses side-by-side and spotting symmetric patterns (e.g., coordinated bot swarms where many wallets behave identically). Best for: mirror and coordination pattern detection.'}
          </div>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.suppLabel}`}>Pyramid — Supplementary</div>
            {'Layered pyramid arrangement. Useful for seeing hierarchical transaction structure: one source distributing to many intermediate wallets distributing to many sinks. Best for: funnel and distribution pattern visualization.'}
          </div>
          <div className={classes.layoutBox}>
            <div className={`${classes.layoutLabel} ${classes.suppLabel}`}>Line — Supplementary</div>
            {'Arranges nodes linearly. Useful for showing a transaction chain from start to finish when the sequence matters more than the topology. Best for: simple traces and fund-flow timelines.'}
          </div>
        </div>
        <div className={classes.callout} style={{ marginTop: 12 }}>
          {'Workflow: Spot a pattern in a supplementary layout → switch to Organic Galaxy to validate it is a real structural cluster and not an artifact of the layout → proceed with investigation.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>3) Data Modes — All / Stories / Watchlist</div>
        <div className={classes.body}>
          {'The toggle at the top of /links controls which transactions are shown in the graph.\n\nAll (default)\n- Full live transaction graph across all monitored addresses and tokens.\n- Use this for broad exploration and discovery: watch what\'s happening across the entire network in the current time window.\n\nStories mode\n- Filters the graph to only show nodes and links involving addresses that appear in recent story alerts.\n- Use this when you want to cross-reference the live graph with E3D\'s automated intelligence findings. Story-address nodes appear highlighted in amber.\n- Story rings appear as colored circles around nodes to indicate which story type detected them.\n\nWatchlist mode\n- Filters the graph to only show transactions involving addresses you have added to your watchlist (bookmarked).\n- Use this to monitor specific wallets or tokens you are actively tracking, without distractions from the full live graph.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>4) Time Windows and LIVE Mode</div>
        <div className={classes.body}>
          {'The time window selector (1M / 2M / 5M / 10M on /links) controls how far back the graph looks.\n\nLIVE mode\n- Streams the most recent 10 minutes of transactions with a 10-second refresh cycle.\n- Watch clusters, hubs, and starburst patterns form in real time.\n- The red LIVE dot indicator turns solid when streaming is active.\n- Click the LIVE button to toggle. LIVE mode disables the time window backward/forward shift controls.\n\nHistorical mode (authenticated users)\n- Use the ◀ ▶ shift buttons to step the window backward/forward by the current window duration.\n- Use the date picker to jump to a specific 10-minute window from the past (up to the lookback limit).\n- Premium+ users have a 30-day lookback; free users have a shorter lookback window.\n\nMin links filter\n- The "Min links" dropdown (0–20) filters out nodes with fewer than N edges in the current window.\n- Increase this to reduce noise and focus on the most connected nodes.\n- Set to 0 to show all nodes including singletons.'}
        </div>
        <div className={classes.calloutWarn}>
          {'LIVE mode is disabled while a historical date range is active. Clear the date range to re-enable live streaming.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) View Filters — What Nodes to Show</div>
        <div className={classes.body}>
          {'The view filter controls which types of nodes are visible in the graph. This is separate from the Data Mode (All/Stories/Watchlist) — you can use any combination.\n\nShow All\n- Tokens, wallets (EOAs), and contracts all visible.\n- Default for broad exploration.\n\nTokens and Connected Users\n- Shows token nodes alongside the wallets that interact with them.\n- Best for: understanding who is trading a specific token.\n\nUsers Only\n- Shows only wallet-to-wallet relationships; removes token nodes.\n- Best for: coordination detection — seeing which wallets transact directly with each other, stripped of the token layer.\n\nTokens Only\n- Shows only token-to-token co-occurrence and correlation.\n- Best for: sector rotation and token co-trading analysis.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>6) Node Investigation — Right-Click Menu</div>
        <div className={classes.body}>
          {'Right-clicking any node opens the investigation context menu. This is the core of the investigation workflow.\n\nNeighborhood controls\n- Show neighbors: expand the graph to include all direct counterparties of this node\n- Hide neighbors: collapse the node\'s counterparties from view\n- Hide non-neighbors: remove everything except this node and its direct connections (isolate the local subgraph)\n- Select neighbors: highlight all counterparties of this node\n\nFollow / Trace\n- Follow Here: re-center the investigation on this node and fetch its transaction history\n- Follow New: open a new trace from this node, incrementing the follow depth\n- Follow depth is capped at 1 hop on /links to prevent runaway expansion\n\nAddress tools\n- Copy Address: copy the node\'s address to clipboard for use in Etherscan, other tools, or Stories search\n- Edit Nametable: label this address with a human-readable name and optional icon. Labels persist across all E3D views and sessions.\n\nThis is the fastest way to build an investigation: isolate a suspicious node → hide non-neighbors → right-click to Follow → check the transaction list for timing and value patterns.'}
        </div>
        <div className={classes.callout}>
          {'Nametable labels you add are private to your account and appear everywhere in E3D — the graph, the node list, and story evidence summaries.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>7) Story Overlay</div>
        <div className={classes.body}>
          {'When Story Overlay is enabled on /links, nodes that have been flagged by recent story alerts are highlighted with colored rings. Each ring color corresponds to a story type:\n\n- Blue ring: FUNNEL story\n- Green ring: CLUSTER\n- Yellow ring: LOOP\n- Purple ring: MIRROR\n- Teal ring: STAGING\n- Red ring: HOTLINKS\n- Gold ring: WHALE\n- Cyan ring: SURGE or FLOW\n- Orange ring: SANDWICH\n\nThis lets you see which live nodes are already under E3D\'s automated surveillance without leaving the graph.\n\nIn Stories data mode, clicking a highlighted node opens a popover showing which stories flagged this address, with direct links to open each story in the /stories feed.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>8) Address Labels and Token Labels</div>
        <div className={classes.body}>
          {'Two separate label controls appear in the graph toolbar:\n\nToken Labels\n- Shows the token symbol (e.g., "USDT", "WETH") on token nodes.\n- Toggle with the Token Labels switch.\n\nAddress Labels\n- Shows shortened addresses (e.g., "0x1234…abcd") on wallet nodes.\n- Toggle with the Address Labels switch.\n\nLabel size\n- The size slider next to the label toggles scales both label types simultaneously.\n\nRecommended workflow:\n- Keep labels off for broad exploration (less visual noise)\n- Turn on Token Labels when you want to identify which tokens are involved in a cluster\n- Turn on Address Labels when you are tracing a specific wallet chain and need to match graph nodes to Etherscan entries'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>9) Sparklines and the Node List</div>
        <div className={classes.body}>
          {'The right panel of the /links view shows a list of the most active nodes in the current window, with sparkline charts showing transaction activity over time.\n\nSparklines\n- Each node in the list has a mini sparkline showing its activity pattern in the current time window.\n- A spike in the sparkline indicates a burst of transactions — a signal worth investigating.\n- Flat sparklines indicate steady background activity.\n\nNode list usage\n- Click any row to center the graph on that node.\n- Use the list to quickly identify the top-activity nodes without clicking through the 3D graph.\n- Sort by links count to find the most connected nodes (potential hubs, routers, or MEV bots).'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>10) Compress / Expand and Camera Controls</div>
        <div className={classes.body}>
          {'Graph density controls\n- Compress: pushes nodes closer together, useful for getting a macro overview of cluster structure when the graph is spread out\n- Expand: spreads nodes apart, useful for untangling a dense hairball to see individual relationships\n\nCamera controls\n- Zoom in/out: scroll wheel or pinch\n- Rotate: click and drag\n- Pan: right-click and drag\n- Zoom to fit: double-click the background to reset the camera to fit all nodes\n\nSpin\n- The graph defaults to a slow camera spin. This is cosmetic; click any node or drag the background to stop the spin and take control of the camera.\n- Spin can be re-enabled from the settings controls.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>11) Recommended Investigation Workflow</div>
        <div className={classes.body}>
          {'A complete investigation from story alert to evidence:\n\nStep 1 — Start in /stories\n- Find the story alert (FUNNEL, CLUSTER, WHALE, SANDWICH, etc.)\n- Open the Evidence Summary to see the story\'s evidence graph and address list\n\nStep 2 — Open the Evidence Graph (/links)\n- From the Evidence Summary, click the "open in new" icon to navigate to /links\n- The graph will pre-load centered on the story\'s primary address\n\nStep 3 — Use Organic Galaxy first\n- Verify the structural pattern (cluster, starburst, funnel) is visible in the primary layout\n- Note: Organic Galaxy is the primary transaction graph. If the pattern disappears in Organic Galaxy, it may be a layout artifact from another view.\n\nStep 4 — Right-click the center node\n- Hide non-neighbors to isolate the local subgraph\n- Check the transaction list for timing patterns and value concentrations\n\nStep 5 — Follow suspicious nodes\n- Use Follow Here to expand a suspicious counterparty\n- Copy its address to check in Etherscan or Stories search\n\nStep 6 — Try a supplementary layout if needed\n- If the pattern is hard to read in Organic Galaxy, try Sphere (for hubs) or Tube (for chains)\n- Return to Organic Galaxy to validate any pattern you find\n\nStep 7 — Label key addresses\n- Right-click → Edit Nametable to label market makers, bots, CEX deposit wallets, etc.\n- These labels persist and will appear in future investigations'}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.body}>
          {'The investigation chain: /stories → Evidence Summary → /links → right-click → Follow → Nametable. This is the fastest path from anomaly detection to actionable evidence.'}
        </div>
      </div>
    </div>
  );
}
